import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Pencil, Trash2, Users } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useToast } from '@/shared/hooks/useToast';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback';
import { ClientDialog } from './ClientDialog';
import { useDeleteClient } from '../hooks/useDeleteClient';
import type { Client } from '../types';

interface ClientRowActionsProps {
  client: Client;
}

export function ClientRowActions({ client }: ClientRowActionsProps) {
  const { t } = useTranslation('clients');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const { toast } = useToast();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const deleteClient = useDeleteClient();
  const [editOpen, setEditOpen] = useState(false);

  const handleViewMembers = () => {
    if (orgId) {
      navigate(`/${orgId}/clients/${client.id}/members`, { state: { clientName: client.name } });
    }
  };

  const handleDelete = useDebouncedCallback(async () => {
    const result = await confirm({
      title: t('confirm.delete'),
      description: t('confirm.deleteDesc', { name: client.name }),
      confirmLabel: t('actions.delete'),
      cancelLabel: t('confirm.cancel'),
      variant: 'destructive',
    });

    if (!result.confirmed) return;

    try {
      await deleteClient.mutateAsync(client.id);
      toast.success({ title: t('toast.deleted') });
    } catch {
      // global handler shows error toast
    }
  });

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                disabled={deleteClient.isPending}
                className="h-7 w-7 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity"
                aria-label={t('actions.openMenu')}
                data-testid={`clients-row-actions-${client.id}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('actions.openMenu')}</TooltipContent>
        </Tooltip>

        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setEditOpen(true)}>
            <Pencil className="w-4 h-4" />
            {t('actions.edit')}
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleViewMembers}>
            <Users className="w-4 h-4" />
            {t('actions.viewMembers')}
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={deleteClient.isPending}
            onClick={handleDelete}
            className="text-destructive focus:text-destructive focus:bg-destructive/10"
          >
            <Trash2 className="w-4 h-4" />
            {t('actions.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ClientDialog open={editOpen} onOpenChange={setEditOpen} client={client} />

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
